import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  GroupButtonComponent,
  LoaderComponent,
  MbsPopupType,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  ModalSettings,
  TreeComponent,
  TreeElement,
  TreeElementDirective
} from 'mbs-ui-kit';
import { CredentialsModalComponent } from 'mbs-ui-shared@components/credentials-modal/credentials-modal.component';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ComputerIcon, GetBackupContentType, MyTreeElements } from 'mbs-ui-shared@models/backup/storages-type';
import { PasswordModalComponent } from 'mbs-ui-shared@modules/password-modal/password-modal.component';
import { BehaviorSubject, Observable } from 'rxjs';
import { skip } from 'rxjs/operators';
import { DataForPath } from 'wizards/helpers/bases/base-for-steps-helper';
import { ExtendedTreeElement, GenerateTreeDataSettings, StepsHelpers, TreeRequestData } from 'wizards/helpers/steps-helpers';
import { RestorePointItem } from 'wizards/models/restore-point-models';
import { TreeIconPath } from 'wizards/models/what-backup-tree-model';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';

import { GetBackupContentParams, ParamsForTreeData, WizardStepsService } from 'wizards/services/wizard-steps.service';

export type ExtendForModalTreeElement = TreeElement & { originalPath: string; path: string };
export type OptionalNetworkSettings = {
  params: ParamsForTreeData | GetBackupContentParams;
  isNetwork: boolean;
  needHide: (items: unknown) => boolean;
};

@UntilDestroy()
@Component({
  selector: 'mbs-tree-in-modal',
  templateUrl: './tree-in-modal.component.html',
  styleUrls: ['./tree-in-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    NgbTooltipModule,
    AlertComponent,
    ModalComponent,
    ModalFooterDirective,
    TreeComponent,
    TreeElementDirective,
    I18NextPipe,
    GroupButtonComponent,
    LoaderComponent
  ]
})
export class TreeInModalComponent implements OnInit {
  private modalService = inject(ModalService);
  private stepService = inject(WizardStepsService);
  private i18nPipe = inject(I18NextPipe);
  private auth = inject(AuthFacade);
  mainService = inject(RemoteManagementWizardsService);

  public readonly mbsPopupType = MbsPopupType;
  public readonly elementsSelector = {
    name: {
      treeElement: 'tree-element',
      nonNtfsIcon: 'non-ntfs-icon',
      errorAlert: 'error-alert',
      confirmButton: 'confirm-button'
    }
  };
  private readonly networkCredentialsError = [1513, 1514];

  public showHint = false;
  public isVirtual = false;
  public title = this.i18nPipe.transform('wizards:tree_modal_default_title', { format: 'title' });
  public optionalNetworkSettings: OptionalNetworkSettings = null;
  public params: ParamsForTreeData | GetBackupContentParams = null;
  public dataForPath: DataForPath = null;
  public hid = '';
  public needOldAsync = false;
  public hideFileType = false;
  public isEncrypted = false;
  public isNetwork = false;
  public isNetworkActive: 0 | 1 = 0;
  public onlyOneSelect = false;
  public oldBackupContentVariant = false;
  public loading = false;
  public loadingInner = false;
  public resultFolders: string[] = [];
  public backupVersionUpdated: string;
  public needHide: (items: unknown) => boolean;
  public notCheckParentIfSelectAll = false;

  public activeParams: ParamsForTreeData | GetBackupContentParams = null;

  public encryptionPasswordHint = '';
  public rootPath = '';
  public separator = ')-(';
  public treeData: TreeElement[] = [];
  public passwordRecoveryEnabled = false;
  public resultPoint: RestorePointItem;

  public allowedRecovery = false;
  public firstLoadError: { title: string; details: string } = null;

  public passwordInvalid = false;

  private isFirstPassError = true;
  private readonly passwordErrorCodes = [2000, 2046];

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;
  @ViewChild(TreeComponent, { static: false }) treeComponent: TreeComponent;

  ngOnInit(): void {
    this.auth.currentUser$.pipe(untilDestroyed(this)).subscribe((user) => {
      this.allowedRecovery = user?.ProviderInfo?.PasswordRecoveryEnabled && user?.IsProvider;
    });

    if (this.baseModal && this.baseModal.data) {
      this.initDataFromBaseModelData();
      this.getBaseTreeData();
    }

    this.mainService.password.pipe(skip(1), untilDestroyed(this)).subscribe((password: string) => {
      this.activeParams.params.Password = password || '';
    });
  }

  initDataFromBaseModelData(): void {
    this.title = this.baseModal.data.title;
    this.hid = this.baseModal.data.hid;
    this.needOldAsync = !!this.baseModal.data.needOldAsync;
    this.resultPoint = this.baseModal.data.resultPoint;
    this.hideFileType = this.baseModal.data.hideFileType;
    this.isVirtual = this.baseModal.data.isVirtual;
    this.optionalNetworkSettings = this.baseModal.data.optionalNetworkSettings;
    this.params = this.baseModal.data.params;
    this.activeParams = this.baseModal.data.params;
    this.rootPath = this.baseModal.data.params.params.path;
    this.resultFolders = Array.from(this.baseModal.data.resultFolders);
    this.dataForPath = this.baseModal.data.dataForPath;
    this.onlyOneSelect = !!this.baseModal.data.onlyOneSelect;
    this.oldBackupContentVariant = !!this.baseModal.data.oldBackupContentVariant;
    this.isEncrypted = !!this.baseModal.data.isEncrypted;
    this.isNetwork = !!this.optionalNetworkSettings?.isNetwork || !!this.baseModal.data.isNetwork;
    this.needHide = this.optionalNetworkSettings?.needHide || this.baseModal.data.needHide;
    this.notCheckParentIfSelectAll = !!this.baseModal.data.notCheckParentIfSelectAll;
    this.encryptionPasswordHint = this.baseModal.data.encryptionPasswordHint;
    this.backupVersionUpdated = this.baseModal.data.backupVersionUpdated;

    this.setParamsForRequests();
  }

  changeViewClickHandler(state: boolean): void {
    this.loadingInner = true;
    this.isNetwork = state;
    this.setParamsForRequests();
    this.getBaseTreeData(true);
    setTimeout(() => (this.isNetworkActive = state ? 1 : 0), 200);
  }

  setParamsForRequests(): void {
    if (!this.optionalNetworkSettings) return void (this.activeParams = this.params);

    this.activeParams = this.isNetwork ? this.optionalNetworkSettings.params : this.params;
  }

  getBaseTreeData(fromUi = false): void {
    if (!fromUi) this.loading = true;

    this.stepService
      .getRemoteCommandData(this.activeParams, this.hid, this.needOldAsync)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => {
          if (data.data?.items) {
            data.data.items = StepsHelpers.getSortedTreeItems(data.data.items, !!data.data.items.length && isNaN(+data.data.items[0].type));

            this.setTreeDataFromRequestItems(data.data.items);
            this.updateResultFoldersIfOnlyOneSelect();
            this.updateTreeDataFormArray();
          } else this.treeData = [];

          this.loading = false;
          this.loadingInner = false;

          if (this.isNetwork && this.resultFolders.length) {
            setTimeout(() => {
              const pathArr = this.resultFolders[0].split(this.dataForPath.join).filter((s) => !!s);
              const compPath = `${this.dataForPath.join}${this.dataForPath.join}${pathArr[0]}`;
              const computer = this.treeComponent?.data?.find((c: any) => c.originalPath === compPath);

              if (computer) this.treeComponent?.getSubtree(computer);
            }, 5);
          }
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          this.loadingInner = false;

          this.baseRequestErrorHandler(err);
        }
      });
  }

  getSubtree(root: ExtendForModalTreeElement): Observable<TreeElement[]> {
    const subtree$ = new BehaviorSubject<TreeElement[]>(null);

    this.activeParams.params.path = this.getPathFromRoot(root);
    this.newSubtreeByParams(subtree$, root);

    return subtree$;
  }

  changeTreeHandler(event: TreeElement): void {
    let childrenShowedCount = 0;
    const str = this.getPathStrFromElementOrParentElement(event);
    const strArr = str?.split(this.dataForPath.join) || [];

    if (event.parent) {
      const strParent = this.getPathStrFromElementOrParentElement(event, true);
      let pathStrings = this.getFilteredPathArray(str, strParent);

      event.children?.forEach((ch) => {
        if (ch.shown) childrenShowedCount++;
        if (ch.disableCheckbox && !event.checked) ch.disableCheckbox = false;
      });
      event.parent?.children?.forEach((c: MyTreeElements) => !c.shown && c.checked && c.path && pathStrings.push(c.path));

      pathStrings = event.checked
        ? this.updatePathStringsIfTreeItemChecked(event, pathStrings, strParent)
        : this.updatePathStringsIfTreeItemNotChecked(event, pathStrings);

      if (event.checked && event.totalChildren > childrenShowedCount) event.children.forEach((ch) => (ch.disableCheckbox = true));

      this.resultFolders = [...new Set(pathStrings)];

      return void this.updateTreeAfterChange(event);
    }

    const pathStrings = this.resultFolders.filter((s) =>
      this.onlyOneSelect
        ? false
        : StepsHelpers.pathNotIncludePath(this.dataForPath.isUnix, this.dataForPath.join, str, s, strArr) &&
          StepsHelpers.pathNotIncludePath(this.dataForPath.isUnix, this.dataForPath.join, s, str, null, strArr)
    );

    event.children?.forEach((ch) => {
      if (ch.shown) childrenShowedCount++;
      if (ch.disableCheckbox && !event.checked) ch.disableCheckbox = false;
    });

    if (event.checked) {
      pathStrings.push(str);
      if (event.totalChildren > childrenShowedCount) event.children.forEach((ch) => (ch.disableCheckbox = true));
    }

    this.resultFolders = pathStrings;

    this.updateTreeAfterChange(event);
  }

  save(): void {
    this.baseModal.save(this.resultFolders);
  }

  close(): void {
    this.baseModal.close();
  }

  // Private functions block
  private updateAllParentItems(parent: TreeElement, pathStrings: string[]): string[] {
    parent.children.forEach((c) => {
      const shownCheckedAndNotIndeterminate = c.shown && c.checked && (!c.children?.length || !c.indeterminate);

      if (shownCheckedAndNotIndeterminate) {
        pathStrings.push(
          StepsHelpers.getPathInStringFormatByOs(
            c,
            this.dataForPath.isUnix,
            !this.oldBackupContentVariant,
            this.isVirtual,
            false,
            false,
            true
          )
        );
      }
    });

    const parentStrLen = (parent.label as string).length + 1;
    const newPathStrings = pathStrings.filter((str) => {
      return parent?.parent
        ? !str.endsWith(parent.label as string) || str[str.length - parentStrLen] !== this.dataForPath.join
        : str !== parent.label;
    });

    if (parent?.parent) return this.updateAllParentItems(parent.parent, newPathStrings);

    return newPathStrings;
  }

  private updateAllParentItemsChecked(parent: TreeElement, strParent = ''): string {
    if (strParent) {
      parent.checked = true;
      parent.indeterminate = false;
    }

    if (parent.parent && !parent.parent.children.some((c) => c.shown && !c.checked)) {
      return this.updateAllParentItemsChecked(parent.parent, this.getPathStrFromElementOrParentElement(parent, true));
    }

    return strParent.replace(this.rootPath, '');
  }

  private getIndeterminateOrCheckedParent(item: ExtendedTreeElement, stringsArray: string[]): ExtendedTreeElement {
    if (!item.parent) return item;

    const existPath = stringsArray.some((s) => StepsHelpers.equalStrings(s, this.getPathFromItem(item), this.dataForPath.isUnix));

    if ((item.indeterminate || !item.checked) && existPath) return item;

    return this.getIndeterminateOrCheckedParent(item.parent, stringsArray);
  }

  private updateAllChildren(children: ExtendedTreeElement[], stringsArray: string[]): void {
    children.forEach((c) => {
      if (c.shown && c.checked) stringsArray.push(this.getPathFromItem(c));
      if (c.children?.length) this.updateAllChildren(c.children, stringsArray);
    });
  }

  private switchToFalseAllChildren(children: ExtendedTreeElement[]): void {
    children.forEach((c) => {
      if (c.shown) c.checked = false;
      if (c.children?.length) this.switchToFalseAllChildren(c.children);
    });
  }

  private getPathFromItem(item: ExtendedTreeElement): string {
    if (this.isVirtual) return (item as ExtendForModalTreeElement).originalPath || (item as ExtendForModalTreeElement).path;

    return (item as ExtendForModalTreeElement).path?.replace(this.rootPath, '') || (item.label as string).replace(this.rootPath, '');
  }

  private updateTreeAfterChange(event: TreeElement): void {
    if (!this.onlyOneSelect) return;

    if (!event.checked) {
      let parent: TreeElement = event.parent || event;

      while (parent?.parent) {
        parent.checked = false;
        parent.indeterminate = false;
        parent = parent.parent;
      }
    }

    if (event.children?.every((c) => c.checked)) this.switchToFalseAllChildren(event.children);
  }

  private updatePathStringsIfTreeItemNotChecked(event: TreeElement, strings: string[]): string[] {
    let pathStrings = [...strings];

    if (!event.parent.indeterminate && !event.parent.checked && event.parent.parent) {
      const indeterminateOrChecked = this.getIndeterminateOrCheckedParent(event.parent, pathStrings);
      const newStr = this.getPathFromItem(indeterminateOrChecked);

      pathStrings = pathStrings.filter((s) => {
        return (
          !StepsHelpers.equalStrings(s, newStr, this.dataForPath.isUnix) &&
          StepsHelpers.pathNotIncludePath(this.dataForPath.isUnix, this.dataForPath.join, newStr, s)
        );
      });

      this.updateAllChildren(indeterminateOrChecked.children, pathStrings);
    }

    if (!event.parent.children.some((c) => c.shown && c.checked && c.id !== event.id)) {
      StepsHelpers.uncheckAllParent(event.parent, true, true);

      return pathStrings;
    }

    if (!this.onlyOneSelect) {
      pathStrings = this.updateAllParentItems(event.parent, pathStrings);
    } else {
      event.parent.children.forEach((c) => (c.checked = false));
      StepsHelpers.uncheckAllParent(event.parent, true, true);
    }

    return pathStrings;
  }

  private setTreeDataFromRequestItems(items: any[]): void {
    this.treeData = items.map((item, idx) => {
      const pathArray = item.path.split(this.dataForPath.join);
      const isAlt = isNaN(+item.type);
      const isVirtualOrUnix = this.isVirtual || this.dataForPath.isUnix;
      let gotChildren = isAlt ? !item.type || item.type === 'File' : +item.type === 3;
      const originalPath = isAlt
        ? pathArray[pathArray.length - 1].toUpperCase() + this.dataForPath.join
        : isVirtualOrUnix || this.isNetwork
          ? item.path
          : item.path.toUpperCase();

      if (this.isVirtual && !item.isCanExcludeContent) {
        gotChildren = true;
      }

      return {
        label:
          isVirtualOrUnix || item.type === GetBackupContentType.Folder || item.type === GetBackupContentType.File
            ? item.displayName
            : originalPath,
        originalPath: originalPath,
        id: `disk-${idx}`,
        icon: isAlt
          ? StepsHelpers.getIconFromType(item.type, isAlt)
          : +item.type === 1
            ? TreeIconPath.Folder
            : this.isNetwork
              ? ComputerIcon
              : TreeIconPath[+item.type || 'Disk'],
        gotChildren,
        hideCheckbox: this.needHide ? this.needHide(item) : false,
        showNTFSAlert: this.isVirtual && !item.isCanExcludeContent,
        checked: false,
        expanded: false,
        shown: true
      };
    });
  }

  private updateResultFoldersIfOnlyOneSelect(): void {
    if (!this.onlyOneSelect) return;

    const lastIsJoin = this.resultFolders[0]?.endsWith(this.dataForPath.join);
    const isRoot =
      this.resultFolders[0] === this.dataForPath.join ||
      (this.resultFolders[0]?.length === 3 && /^([a-zA-Z]:\\)/.test(this.resultFolders[0]) && lastIsJoin);

    if ((this.treeData[0] as any)?.originalPath?.length === 2 && !isRoot && lastIsJoin) {
      this.resultFolders[0] = this.resultFolders[0].slice(0, -1);
    }
  }

  private baseRequestErrorHandler(error: HttpErrorResponse): void {
    if (this.isEncrypted && error?.error?.errorCode && this.passwordErrorCodes.includes(+error.error.errorCode)) {
      return void this.passErrorHandler(+error.error.errorCode);
    }

    const title = error.error?.title || (error as any).title;
    const message = title ? error.error?.detail : error.error?.message || error.message;

    delete this.title;
    this.firstLoadError = { title: message ? title : undefined, details: message || title };
  }

  private passErrorHandler(code: number, root: TreeElement = null, subtree$: BehaviorSubject<TreeElement[]> = null): void {
    if (code !== 2000) return void this.showPasswordModal(root, subtree$);

    if (!this.isFirstPassError) this.passwordInvalid = true;

    this.isFirstPassError = false;

    this.showPasswordModal(root, subtree$);
  }

  private updateTreeDataFormArray(): void {
    const functionName = this.oldBackupContentVariant ? 'updateTreeItemFromOldRequest' : 'updateTreeItemFromNewRequest';

    this.treeData.forEach((item: TreeElement) => this[functionName](item));
  }

  private updateTreeItemFromNewRequest(item: TreeElement): void {
    const path = this.dataForPath.isUnix || this.isVirtual ? (item as ExtendForModalTreeElement).originalPath : (item.label as string);
    const itemPathLen = path?.length || 0;
    const existStr = StepsHelpers.findStrForGetSubtree(this.resultFolders, path, this.dataForPath.join, this.dataForPath.isUnix, true);
    const existLen = existStr?.length || 0;

    item.checked = !!existStr && existLen === itemPathLen;

    if (this.dataForPath.isUnix && path === this.dataForPath.join) {
      const newInc = StepsHelpers.findInNotRootFolder(this.resultFolders, this.treeData);
      item.indeterminate = !!newInc.length && !item.checked;

      return;
    }

    item.indeterminate = existLen > itemPathLen && !item.checked;
  }

  private updateTreeItemFromOldRequest(item: TreeElement): void {
    const isUnix = this.dataForPath.isUnix;
    const joinStr = this.dataForPath.join;
    const path = isUnix ? (item as ExtendForModalTreeElement).originalPath : (item.label as string);

    if (isUnix && path === joinStr) {
      const newInc = StepsHelpers.findInNotRootFolder(this.resultFolders, this.treeData, true);
      item.checked = (this.onlyOneSelect && !!newInc.length) || this.resultFolders.some((str) => str === path);
      item.indeterminate = !!newInc.length && !item.checked;

      return;
    }

    const incCurrent = this.resultFolders.some((incStr) => !StepsHelpers.pathNotIncludePath(isUnix, joinStr, incStr, path));
    item.checked = this.onlyOneSelect
      ? this.resultFolders.some(
          (s) => StepsHelpers.equalStrings(path, s, isUnix) || !StepsHelpers.pathNotIncludePath(isUnix, joinStr, s, path)
        )
      : this.resultFolders.some((s) => StepsHelpers.equalStrings(path, s, isUnix));
    item.indeterminate = incCurrent && !item.checked;
  }

  private getPathFromRoot(root: ExtendForModalTreeElement): string {
    if (!root.parent) {
      return !this.isVirtual && !this.oldBackupContentVariant ? `${this.rootPath}\\${root.originalPath}` : root.originalPath;
    }

    return (root as any).path;
  }

  private newSubtreeByParams(subtree$: BehaviorSubject<TreeElement[]>, root: MyTreeElements = null): void {
    this.activeParams.params.offset = root?.children
      ? root.children.reduce((acc, next) => {
          if (next.shown || next.shown !== false) acc++;
          return acc;
        }, 0)
      : 0;

    this.stepService
      .getRemoteCommandData(this.activeParams, this.hid, this.needOldAsync)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => subtree$.next(this.getTreeItemsAndUpdateRoot(data, root)),
        error: (error: HttpErrorResponse) => {
          const code = +error?.error?.errorCode;
          if (this.isNetwork && this.networkCredentialsError.includes(code)) {
            return void this.openShareCredentialsModal(root, subtree$);
          }

          if (this.isEncrypted && error?.error?.errorCode && this.passwordErrorCodes.includes(code)) {
            return void this.passErrorHandler(+error.error.errorCode, root, subtree$);
          }

          subtree$.next([]);
        }
      });
  }

  private openShareCredentialsModal(root: TreeElement, subtree$: BehaviorSubject<TreeElement[]>): void {
    const settings: ModalSettings = { isCustom: true, data: { root } };

    this.modalService
      .openCustom(CredentialsModalComponent, settings)
      .then((result: { login: string; password: string }) => {
        this.activeParams.params.login = result.login;
        this.activeParams.params.password = result.password;
        this.newSubtreeByParams(subtree$, root);
      })
      .catch(() => {
        root.loadingChildren = false;
        this.treeComponent.loadingSubtree = false;
      });
  }

  private getTreeItemsAndUpdateRoot(data: TreeRequestData, root: MyTreeElements): TreeElement[] {
    const settings = this.getSettingsForTreeHelper(root, data);
    const newResultForm = this.resultFolders.map((str) => (!this.isVirtual && !this.isNetwork ? this.rootPath + str : str));

    if (data?.data?.items?.length) {
      data.data.items = StepsHelpers.getSortedTreeItems(data.data.items, settings.alt);
    }

    if (this.activeParams?.params?.offset) {
      const pathMap = {};
      data.data.items.forEach((el) => (pathMap[el.restorePath] = true));
      root.children = root.children.filter((ch: MyTreeElements) => ch.shown || !pathMap[ch.path]);
    }

    const treeData = StepsHelpers.generateTreeData(data, newResultForm, [], settings).filter((f) => (this.hideFileType ? f.shown : true));

    if (root.hideCheckbox && root.icon === ComputerIcon) {
      treeData.forEach((el) => (el.gotChildren = true));
    }

    if (this.notCheckParentIfSelectAll) this.updateNewTreeElementsIfNotCheckParentIfSelectAll(treeData, newResultForm, root);

    this.onlyOneSelectUpdate(root, treeData);

    if (this.hideFileType && data?.data?.items.length === data?.data?.totalCount) {
      root.totalChildren = (treeData.length || 0) - 1;
    } else {
      root.totalChildren = StepsHelpers.getTotalChildren(data?.data, treeData.length);
    }

    if (root.children?.[0]?.disableCheckbox && treeData?.length + this.activeParams?.params?.offset >= root?.totalChildren) {
      root.children.forEach((el) => (el.disableCheckbox = false));
      treeData.forEach((el) => (el.disableCheckbox = false));
    }

    return treeData;
  }

  private getSettingsForTreeHelper(root: MyTreeElements, data: TreeRequestData): GenerateTreeDataSettings {
    return {
      root,
      alt: !!data?.data?.items?.length && isNaN(+data.data.items[0].type),
      newInc: [],
      newExc: [],
      isManually: false,
      joinStr: this.dataForPath.join,
      sep: this.separator,
      hideFileType: this.hideFileType,
      onlyOneSelect: this.onlyOneSelect,
      isUnix: this.dataForPath.isUnix,
      isVirtual: this.isVirtual,
      needHide: this.needHide,
      disableCheckbox:
        root.checked &&
        !root.indeterminate &&
        (root.disableCheckbox ||
          (data?.data?.totalCount && data.data.totalCount > data.data.items?.length + this.activeParams.params.offset))
    };
  }

  private updateNewTreeElementsIfNotCheckParentIfSelectAll(treeData: ExtendedTreeElement[], folders: string[], root: TreeElement): void {
    const rootStr = this.getPathStrFromElementOrParentElement(root, false);

    /**
     * Add mock hidden element for force root indeterminate state if:
     * 1) Root indeterminate
     * 2) Root is not checked
     * 3) All root children is checked and not indeterminate
     * 4) Not exist path into folders list equal root path
     * */
    const needPushVoid =
      root.indeterminate &&
      !root.checked &&
      treeData.every((c) => c.checked && !c.indeterminate) &&
      !folders.some((s) => StepsHelpers.equalStrings(s, rootStr, this.dataForPath.isUnix));

    if (needPushVoid) treeData.push({ label: '', id: root.id + this.separator, shown: false });
  }

  private onlyOneSelectUpdate(root, treeData): void {
    if (this.onlyOneSelect) treeData.forEach((e) => (e.indeterminate = false));

    if ((this.onlyOneSelect || root.onlyOneSelect) && root.checked && !treeData.some((e) => !e.shown)) {
      treeData.push({
        label: '',
        id: root.id + this.separator,
        shown: false,
        checked: this.onlyOneSelect && !treeData.some((e) => e.checked)
      });
    }

    if (
      this.onlyOneSelect &&
      treeData.every((c) => !c.shown || c.checked) &&
      this.resultFolders.some((s) => StepsHelpers.equalStrings(s, root.originalPath || this.getPathFromItem(root), this.dataForPath.isUnix))
    ) {
      treeData.forEach((c) => (c.checked = !c.shown));
    }
  }

  private showPasswordModal(root: TreeElement = null, subtree$: BehaviorSubject<TreeElement[]> = null): void {
    this.modalService
      .openCustom(PasswordModalComponent, this.getSettingsForPasswordModal())
      .then((result: { password: FormControl<string> }) => this.actionsAfterSetPasswordFromModal(root, subtree$, result))
      .catch(() => {
        if (root && subtree$) {
          this.mainService.password.next('');

          return void subtree$.error(1);
        }

        this.close();
      });
  }

  private getSettingsForPasswordModal(): ModalSettings {
    return {
      data: {
        password: this.mainService.password.value || '',
        hid: this.mainService.hid,
        passwordInvalid: this.passwordInvalid,
        showHint: false,
        passwordRecoveryEnabled: this.allowedRecovery && this.mainService.isNBF && !this.dataForPath.isUnix,
        backupVersionUpdated: this.mainService.backupVersionUpdated,
        restorePoint: this.resultPoint
      },
      collapsing: true
    };
  }

  private actionsAfterSetPasswordFromModal(
    root: TreeElement = null,
    subtree$: BehaviorSubject<TreeElement[]> = null,
    result: { password: FormControl<string> }
  ): void {
    const passControl = result.password;

    this.mainService.password.next(passControl.value);
    this.activeParams.params.Password = passControl.value;

    if (passControl.valid) {
      root && subtree$ ? this.newSubtreeByParams(subtree$, root) : this.getBaseTreeData();

      return;
    }

    if (root && subtree$) {
      this.mainService.password.next('');

      return void subtree$.error(1);
    }

    this.close();
  }

  private getPathStrFromElementOrParentElement(event: TreeElement, isParent = false): string {
    if (isParent) {
      return (
        (this.isVirtual || this.dataForPath.isUnix
          ? (event.parent as ExtendForModalTreeElement).originalPath || (event.parent as ExtendForModalTreeElement).path
          : (event.parent as ExtendForModalTreeElement).path?.replace(this.rootPath, '')) ||
        (event.parent.label as string).replace(this.rootPath, '')
      );
    }

    return (
      ((this.isVirtual || this.dataForPath.isUnix
        ? (event as ExtendForModalTreeElement).originalPath || (event as ExtendForModalTreeElement).path
        : (event as ExtendForModalTreeElement).path) || event.label) as string
    ).replace(this.rootPath, '');
  }

  private getFilteredPathArray(str: string, strParent: string): string[] {
    const strParLen =
      !this.isVirtual && !strParent.startsWith(this.dataForPath.join) ? (this.dataForPath.join + strParent).length : strParent.length;

    if (this.onlyOneSelect) return [];

    return this.resultFolders.filter((s) => {
      const sLast = s[strParLen];

      if (!s.includes(strParent) || (sLast && sLast !== this.dataForPath.join)) return true;

      if (!StepsHelpers.pathNotIncludePath(this.dataForPath.isUnix, this.dataForPath.join, s, str)) return false;

      const sStartJoinSymbol = s.startsWith(this.dataForPath.join);
      const strParentStartJoinSymbol = strParent.startsWith(this.dataForPath.join);

      if (sStartJoinSymbol === strParentStartJoinSymbol) {
        return s.split(this.dataForPath.join).length - strParent.split(this.dataForPath.join).length > 1;
      }

      return sStartJoinSymbol
        ? s.split(this.dataForPath.join).length - strParent.split(this.dataForPath.join).length > 2
        : s.split(this.dataForPath.join).length - strParent.split(this.dataForPath.join).length > 0;
    });
  }

  private updatePathStringsIfTreeItemChecked(event: TreeElement, strings: string[], strParent: string): string[] {
    let pathStrings = [...strings];

    if (this.notCheckParentIfSelectAll) {
      this.updateTreeIfExistParentAndCheckedNodeNotCheckParent(pathStrings, event);

      return pathStrings;
    }

    if (!this.onlyOneSelect && !event.parent.children.some((c) => c.shown && !c.checked)) {
      const newParentStr = this.updateAllParentItemsChecked(event.parent, strParent);

      pathStrings = pathStrings.filter(
        (s) =>
          StepsHelpers.pathNotIncludePath(this.dataForPath.isUnix, this.dataForPath.join, s, newParentStr) &&
          StepsHelpers.pathNotIncludePath(this.dataForPath.isUnix, this.dataForPath.join, s, this.dataForPath.join + newParentStr)
      );

      pathStrings.push(newParentStr);

      return pathStrings;
    }

    this.updatePathArrayFromChildren(pathStrings, event.parent.children);

    if (this.onlyOneSelect && event.children?.length && !event.children.some((e) => !e.shown)) {
      event.children.push({ label: '', id: event.id + this.separator, shown: false, checked: true });

      this.switchToFalseAllChildren(event.children);
    }

    return pathStrings;
  }

  private updateTreeIfExistParentAndCheckedNodeNotCheckParent(pathStrings: string[], event: TreeElement): void {
    this.updatePathArrayFromChildren(pathStrings, event.parent.children);
    StepsHelpers.uncheckAllParent(event.parent, true, true);
  }

  private updatePathArrayFromChildren(pathStrings: string[], children: TreeElement[]): void {
    children.forEach(
      (c) =>
        c.shown &&
        c.checked &&
        pathStrings.push(
          this.isNetwork
            ? (c as any).path
            : StepsHelpers.getPathInStringFormatByOs(
                c,
                this.dataForPath.isUnix,
                !this.oldBackupContentVariant,
                this.isVirtual,
                this.dataForPath.isUnix && this.oldBackupContentVariant,
                false,
                true
              )
        )
    );
  }
}
